// Weekly ready-date table (reported issues 3.c). A store works out Special /
// Regular ready dates either from days to deliver (DAYS_TO_DELIVER, the
// original rules in checkoutDateRules.ts) or from this table (WEEKLY_TABLE):
// for each drop-off day, the weekday a ticket is ready before and after the
// cut-off time. Only one mode is active at a time.

export type ReadyDateMode = "DAYS_TO_DELIVER" | "WEEKLY_TABLE";

export const READY_DATE_MODE_DAYS: ReadyDateMode = "DAYS_TO_DELIVER";
export const READY_DATE_MODE_TABLE: ReadyDateMode = "WEEKLY_TABLE";

/** A weekday ("MONDAY" …) or null for Closed. */
export type ReadyDay = string | null;

export interface IReadyDateRule {
  dropOffDay: string;
  beforeCutoffSpecial: ReadyDay;
  beforeCutoffRegular: ReadyDay;
  afterCutoffSpecial: ReadyDay;
  afterCutoffRegular: ReadyDay;
}

export const WEEK_DAYS = [
  "SUNDAY",
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
];

const rule = (
  dropOffDay: string,
  beforeCutoffSpecial: ReadyDay,
  beforeCutoffRegular: ReadyDay,
  afterCutoffSpecial: ReadyDay,
  afterCutoffRegular: ReadyDay
): IReadyDateRule => ({
  dropOffDay,
  beforeCutoffSpecial,
  beforeCutoffRegular,
  afterCutoffSpecial,
  afterCutoffRegular,
});

/** The client's table, used to fill the admin editor when none is saved. */
export const DEFAULT_READY_DATE_RULES: IReadyDateRule[] = [
  rule("MONDAY", "MONDAY", "WEDNESDAY", "TUESDAY", "THURSDAY"),
  rule("TUESDAY", "TUESDAY", "THURSDAY", "WEDNESDAY", "FRIDAY"),
  rule("WEDNESDAY", "WEDNESDAY", "FRIDAY", "THURSDAY", "MONDAY"),
  rule("THURSDAY", "THURSDAY", "MONDAY", "FRIDAY", "TUESDAY"),
  rule("FRIDAY", "FRIDAY", "TUESDAY", "SATURDAY", "TUESDAY"),
  rule("SATURDAY", "SATURDAY", "TUESDAY", "MONDAY", "WEDNESDAY"),
  rule("SUNDAY", null, null, null, null),
];

/** The saved table, with the default row for any day it doesn't have. */
export const withDefaultRows = (saved?: IReadyDateRule[] | null): IReadyDateRule[] =>
  DEFAULT_READY_DATE_RULES.map(
    (row) => saved?.find((r) => r.dropOffDay === row.dropOffDay) ?? row
  );

// Set from the store config when it loads (like the store time zone), so
// every getCheckOutDate caller follows the active mode
let activeRules: IReadyDateRule[] | null = null;

// A service's own ready time (Workflow page), "HH:mm:ss"; null = pickupTime
let serviceReadyTimes: { regular: string | null; special: string | null } = {
  regular: null,
  special: null,
};

export const setServiceReadyTimes = (
  regular?: string | null,
  special?: string | null
) => {
  serviceReadyTimes = { regular: regular || null, special: special || null };
};

/** The ready time a service uses instead of the store pickup time, if any. */
export const serviceReadyTime = (isSpecial: boolean) =>
  isSpecial ? serviceReadyTimes.special : serviceReadyTimes.regular;

export const setReadyDateConfig = (
  mode?: string | null,
  rules?: IReadyDateRule[] | null
) => {
  activeRules =
    mode === READY_DATE_MODE_TABLE && rules && rules.length ? rules : null;
};

/** The weekly table when it is the active mode, else null. */
export const activeReadyDateRules = () => activeRules;

const addDays = (date: Date, days: number) => {
  const next = new Date(date);
  next.setDate(date.getDate() + days);
  return next;
};

/**
 * The ready date (at `now`'s time of day) from the weekly table, or null if
 * every day is Closed. A drop-off on a Closed day, a non-working day or a
 * holiday counts as a drop-off at the next open day, before its cut-off.
 *
 * The table gives the ready weekday; the working days up to it are how long
 * the ticket takes. Holidays in between don't count, so the ready date moves
 * past them, and never lands on a non-working day or a holiday.
 */
export const tableReadyDate = (
  now: Date,
  isSpecial: boolean,
  beforeCutoff: boolean,
  rules: IReadyDateRule[],
  isWorkingDay: (date: Date) => boolean = () => true,
  isClosedDate: (date: Date) => boolean = () => false,
  onHolidaySkipped: (date: Date) => void = () => {}
): Date | null => {
  const isOpen = (date: Date) => isWorkingDay(date) && !isClosedDate(date);
  for (let shift = 0; shift < 14; shift++) {
    const dropOff = addDays(now, shift);
    if (!isOpen(dropOff)) {
      if (isClosedDate(dropOff)) onHolidaySkipped(dropOff);
      continue;
    }
    const row = rules.find((r) => r.dropOffDay === WEEK_DAYS[dropOff.getDay()]);
    const before = shift === 0 ? beforeCutoff : true;
    const readyDay = !row
      ? null
      : isSpecial
        ? before
          ? row.beforeCutoffSpecial
          : row.afterCutoffSpecial
        : before
          ? row.beforeCutoffRegular
          : row.afterCutoffRegular;
    const readyIndex = readyDay ? WEEK_DAYS.indexOf(readyDay) : -1;
    if (readyIndex === -1) continue;
    const offset = (readyIndex - dropOff.getDay() + 7) % 7;
    if (offset === 0) return dropOff;

    // Working days the table allows: those before the ready day, plus the
    // ready day itself (moved to the next open day if it isn't one)
    let daysNeeded = 1;
    for (let i = 1; i < offset; i++) {
      if (isWorkingDay(addDays(dropOff, i))) daysNeeded++;
    }
    let ready = dropOff;
    for (let counted = 0, guard = 0; counted < daysNeeded && guard < 60; guard++) {
      ready = addDays(ready, 1);
      if (isOpen(ready)) counted++;
      else if (isClosedDate(ready)) onHolidaySkipped(ready);
    }
    return ready;
  }
  return null;
};
